import { lazy, Suspense } from 'react';
import { Link } from '@/components/LocalizedLink';
import Hero from '@/components/Hero';
import SEOHead from '@/components/SEOHead';
import { buildSpeakableSchema } from '@/lib/schema';
import { useLanguage } from '@/contexts/LanguageContext';
import { useQuery } from '@tanstack/react-query';
import { fetchHomeContent, fetchSettingsContent, getLocalizedText } from '@/lib/contentService';
import {
  getFeaturedServiceSlugs,
  getServicesBySlugs,
  localizeServiceText,
} from '@/lib/serviceCatalog';

// Below-the-fold home sections — split out of the initial bundle. Hero (and
// everything above) stays eager since it's what LCP/first paint measures;
// these load once the browser is idle. Prerender (scripts/prerender-routes.mjs)
// waits for `networkidle`, so the static SEO snapshot still includes them.
const Marquee = lazy(() => import('@/components/Marquee'));
const ServicesSection = lazy(() => import('@/components/ServicesSection'));
const ProcessSection = lazy(() => import('@/components/ProcessSection'));
const ProjectsSection = lazy(() => import('@/components/ProjectsSection'));
const TestimonialsSection = lazy(() => import('@/components/TestimonialsSection'));
const TrustFactsBlock = lazy(() => import('@/components/TrustFactsBlock'));
const TechStackSection = lazy(() => import('@/components/TechStackSection'));
const BlogSection = lazy(() => import('@/components/BlogSection'));
const AboutSection = lazy(() => import('@/components/AboutSection'));
const CTASection = lazy(() => import('@/components/CTASection'));
const ContactSection = lazy(() => import('@/components/ContactSection'));

const Index = () => {
  const { t, lang } = useLanguage();
  const { data: homeContent } = useQuery({
    queryKey: ['singleton', 'home', 'index'],
    queryFn: fetchHomeContent,
  });
  const { data: settings } = useQuery({
    queryKey: ['singleton', 'settings'],
    queryFn: fetchSettingsContent,
  });

  const marqueeText = homeContent?.references?.length
    ? ` ${homeContent.references.join(' • ')} `
    : t('marquee.text');

  const processFallbackSteps = ['step1', 'step2', 'step3', 'step4'].map((step) => ({
    title: t(`process.${step}.title`),
    description: t(`process.${step}.desc`),
  }));

  const aboutFallbackValues = ['value1', 'value2', 'value3'].map((value) => ({
    title: t(`about.${value}.title`),
    description: t(`about.${value}.desc`),
  }));
  const featuredServices = getServicesBySlugs(getFeaturedServiceSlugs());

  return (
    <main>
      <SEOHead
        canonical="/"
        jsonLd={buildSpeakableSchema({ canonicalPath: '/', locale: lang })}
      />
      <Hero
        primaryCtaText={getLocalizedText(settings?.ctaPrimaryText, lang)}
        primaryCtaUrl={settings?.ctaPrimaryUrl}
        secondaryCtaText={getLocalizedText(settings?.ctaSecondaryText, lang)}
        secondaryCtaUrl={settings?.ctaSecondaryUrl}
      />
      <section
        className="container mx-auto px-6 lg:px-12 pt-2 pb-3 relative z-10"
        aria-label={lang === 'tr' ? 'Figtures özeti' : 'Figtures summary'}
      >
        <p
          id="geo-direct-answer"
          className="geo-direct-answer font-body text-[11px] sm:text-xs text-muted-foreground/60 text-center max-w-2xl mx-auto leading-relaxed tracking-wide"
        >
          {t('home.geoDirectAnswer')}
        </p>
      </section>
      <section className="container mx-auto px-6 lg:px-12 pb-2 relative z-10">
        <nav
          aria-label={lang === 'tr' ? 'Öne çıkan bağlantılar' : 'Featured links'}
          className="flex flex-wrap gap-2 max-w-3xl mx-auto justify-center"
        >
          {featuredServices.map((service) => (
            <Link
              key={service.slug}
              to={`/services/${service.slug}`}
              className="font-body text-xs px-3 py-1.5 rounded-full border border-border/80 text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
            >
              {localizeServiceText(service.shortLabel, lang)}
            </Link>
          ))}
          <Link
            to="/tools"
            className="font-body text-xs px-3 py-1.5 rounded-full border border-border/80 text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
          >
            {t('nav.tools')}
          </Link>
          <Link
            to="/solutions"
            className="font-body text-xs px-3 py-1.5 rounded-full border border-border/80 text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
          >
            {t('nav.solutions')}
          </Link>
          <Link
            to="/ai-answers"
            className="font-body text-xs px-3 py-1.5 rounded-full border border-border/80 text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
          >
            {t('footer.aiAnswers')}
          </Link>
        </nav>
      </section>
      <Suspense fallback={null}>
        <Marquee text={marqueeText} />
        <ServicesSection services={homeContent?.services?.map(s => ({
          title: getLocalizedText(s.title, lang),
          description: getLocalizedText(s.description, lang)
        }))} />
        <ProcessSection
          tag={t('process.tag')}
          title={t('process.title')}
          docs={t('process.docs')}
          steps={processFallbackSteps}
        />
        <ProjectsSection />
        <TestimonialsSection
          tag={getLocalizedText(homeContent?.testimonials.tag, lang, t('testimonials.tag'))}
          title={getLocalizedText(homeContent?.testimonials.title, lang, t('testimonials.title'))}
        />
        <div className="container mx-auto px-6 lg:px-12 section-y-compact">
          <TrustFactsBlock lang={lang} />
        </div>
        <TechStackSection />
        <BlogSection />
        <AboutSection
          tag={getLocalizedText(homeContent?.aboutSection.tag, lang)}
          title={getLocalizedText(homeContent?.aboutSection.title, lang)}
          description={getLocalizedText(homeContent?.aboutSection.description, lang)}
          stats={homeContent?.aboutSection?.stats?.map((stat) => ({
            number: getLocalizedText(stat.number, lang),
            label: getLocalizedText(stat.label, lang),
          }))}
          values={homeContent?.aboutSection?.values?.map((v, index) => ({
            title: getLocalizedText(v.title, lang, aboutFallbackValues[index]?.title || ''),
            description: getLocalizedText(v.description, lang, aboutFallbackValues[index]?.description || ''),
          }))}
        />
        <CTASection
          primaryText={getLocalizedText(settings?.ctaPrimaryText, lang)}
          primaryUrl={settings?.ctaPrimaryUrl}
          secondaryText={getLocalizedText(settings?.ctaSecondaryText, lang)}
          secondaryUrl={settings?.ctaSecondaryUrl}
        />
        <ContactSection />
      </Suspense>
    </main>
  );
};

export default Index;
